/**
 * QUIPWA — Drive search query builder
 *
 * Drive's `q` parameter is a small query language where string literals are
 * wrapped in single quotes. A file name containing `'` (e.g. "Repaso d'inglés")
 * used to be interpolated raw, which either broke the query (400) or — worse —
 * changed its meaning. Every literal goes through `quote()` here.
 *
 * Reference: https://developers.google.com/drive/api/guides/ref-search-terms
 */

/** Escape a value for use inside a single-quoted Drive query literal. */
export function escapeQueryValue(value: string): string {
  return value.replace(/\\/g, '\\\\').replace(/'/g, "\\'")
}

function quote(value: string): string {
  return `'${escapeQueryValue(value)}'`
}

export const q = {
  nameEquals: (name: string) => `name = ${quote(name)}`,
  nameEndsWith: (suffix: string) => `name contains ${quote(suffix)}`,
  inParent: (parentId: string) => `${quote(parentId)} in parents`,
  mimeTypeIs: (mimeType: string) => `mimeType = ${quote(mimeType)}`,
  mimeTypeIsNot: (mimeType: string) => `mimeType != ${quote(mimeType)}`,
  notTrashed: () => 'trashed = false',
  /**
   * Child of any of these folders: `('a' in parents or 'b' in parents)`.
   * One request instead of one per folder; callers keep the list short
   * (see `chunk`) so the URL stays well under Drive's limits.
   */
  anyParent: (parentIds: readonly string[]) =>
    parentIds.length === 0 ? null : `(${parentIds.map((id) => `${quote(id)} in parents`).join(' or ')})`,
  /** AND-join clauses, skipping empty ones. */
  and: (...clauses: Array<string | false | null | undefined>) =>
    clauses.filter((c): c is string => typeof c === 'string' && c.length > 0).join(' and '),
}
